import omit from 'lodash/omit';
import pick from 'lodash/pick';

import {and, buildQueryString, not, or} from '@edna/utils';

import {TData, TPayload} from 'src/components/List';
import {TItemId} from 'src/constants';
import {ERoles} from 'src/containers/User';
import {CACHE_LIFETIME, ECacheTag, rootApi} from 'src/models';
import {booleanHashToArray, mapEmptyFieldsToUndefined} from 'src/utils';

import {
  CUSTOM_TAX_RATE,
  ELeadClientCreatingError,
  ELeadClientStatus,
  ELeadType,
  TAgentClient,
  TAgentFormData,
  TFilters,
  TFormData,
  TLeadClient,
  TLeadClientAnalytics,
  TPartner,
  TTenantLeadDetailsPayload,
} from './definitions';
import {getSelfRegistrationFilter} from './utils';

const prepareFilters = (payload: TFilters) => {
  const {period, statuses, type, agentId, registrationType, ...other} = payload;

  return {
    ...other,
    createdAtTo: period?.stopDate,
    createdAtFrom: period?.startDate,
    statuses: booleanHashToArray(statuses),
    type: type ?? ELeadType.CLIENT,
    agentId: agentId ? Number(agentId) : undefined,
    selfRegistered: getSelfRegistrationFilter(registrationType),
  };
};

const prepareAgentClientFormValues = (values: TAgentFormData) => {
  const customTaxRate = (values.customTaxRate || 0) / 100;
  const taxRate = values.taxRate === CUSTOM_TAX_RATE ? customTaxRate : values.taxRate;

  const restValues = values.id
    ? pick(values, [
        'companyName',
        'signatoryLastName',
        'signatoryFirstName',
        'signatoryMiddleName',
        'signingBasis',
        'signingBasisNumber',
        'signingBasisDate',
      ])
    : omit(values, ['customTaxRate']);

  return mapEmptyFieldsToUndefined({
    ...restValues,
    taxRate,
  });
};

const prepareLeadClientFormValues = (values: TFormData) =>
  mapEmptyFieldsToUndefined({
    ...omit(values, ['hasPartner', 'isAgreedOverdraft']),
    securityDeposit: Number(values.securityDeposit),
    maxOverdraft: Number(values.maxOverdraft),
  });

const api = rootApi.injectEndpoints({
  overrideExisting: 'throw',
  endpoints: (builder) => {
    const createClientsBuilderQuery = <T = ELeadType.CLIENT>() =>
      builder.query<
        TData<T extends ELeadType.AGENT ? TAgentClient : TLeadClient>,
        TPayload<TFilters>
      >({
        query: (data) => ({
          method: 'POST',
          url: '/tenantmanagement/tenant-lead/list',
          data: prepareFilters(data),
        }),
        keepUnusedDataFor: CACHE_LIFETIME,
        providesTags: (result) => {
          if (!result) {
            return [ECacheTag.LEAD_CLIENTS];
          }

          return [
            ECacheTag.LEAD_CLIENTS,
            ...result.content.map(({id}) => ({id, type: ECacheTag.LEAD_CLIENTS})),
          ];
        },
      });

    const checkEmailQuery = async (baseQuery: TAnyFunction, email?: string) => {
      if (!email) {
        return {data: true};
      }

      const {data: leadClientData}: {data: TLeadClient | TAgentClient} = await baseQuery({
        method: 'GET',
        url: `/tenantmanagement/tenant-lead/email/${email}`,
      });

      if (leadClientData && leadClientData.status !== ELeadClientStatus.DRAFT) {
        return {error: true};
      }

      const {data: tenantData} = await baseQuery({
        method: 'GET',
        url: `/tenantmanagement/tenant/email/${email}`,
      });

      if (tenantData) {
        return {error: true};
      }

      const {data: userData} = await baseQuery({
        method: 'GET',
        url: `/user/management/login/${email}`,
      });

      if (userData) {
        return {error: true};
      }

      return {data: true};
    };

    return {
      getLeadClients: createClientsBuilderQuery<ELeadType.CLIENT>(),
      getLeadClientsAnalytics: builder.query<TLeadClientAnalytics, TPayload<TFilters>>({
        query: (data) => ({
          method: 'POST',
          url: '/tenantmanagement/tenant-lead/count',
          data: prepareFilters(data),
        }),
        keepUnusedDataFor: CACHE_LIFETIME,
        providesTags: [ECacheTag.LEAD_CLIENTS],
      }),
      getLeadClient: builder.query<TLeadClient, TItemId>({
        query: (id) => ({
          method: 'GET',
          url: `/tenantmanagement/tenant-lead/${id}`,
        }),
        providesTags: (_, __, id) => [{id, type: ECacheTag.LEAD_CLIENTS}],
      }),
      createLeadClient: builder.mutation<TLeadClient, TFormData>({
        query: ({id, ...data}) => ({
          method: id ? 'PUT' : 'POST',
          url: id
            ? `/tenantmanagement/tenant-lead/${id}/from-draft`
            : '/tenantmanagement/tenant-lead/',
          data: prepareLeadClientFormValues(data),
          meta: {
            isShowError: true,
            errorMessageKey: 'List:errors.failedToSaveItem',
          },
        }),
        invalidatesTags: (_, __, {id}) => [{id, type: ECacheTag.LEAD_CLIENTS}],
      }),
      getAgentClients: createClientsBuilderQuery<ELeadType.AGENT>(),
      getAgentClient: builder.query<TAgentClient, {id?: TItemId; tenantId?: TItemId}>({
        query: ({id, tenantId}) => ({
          method: 'GET',
          url: tenantId
            ? `/tenantmanagement/agent/${tenantId}`
            : `/tenantmanagement/agent-lead/${id}`,
        }),
        providesTags: (_, __, {id}) => [{id, type: ECacheTag.LEAD_CLIENTS}],
      }),
      getPartners: builder.query<TData<TPartner>, TPayload<{filter?: string}>>({
        query: (data) => ({
          method: 'POST',
          url: '/tenantmanagement/agent/list-names',
          data,
          meta: {
            isShowError: true,
            roles: or<ERoles>('ROLE_ACCOUNT_MANAGER', 'ROLE_AGENT'),
          },
        }),
      }),
      saveAgentClient: builder.mutation<TAgentClient, TAgentFormData>({
        query: (data) => ({
          method: data.id ? 'PUT' : 'POST',
          url: data.id ? `/tenantmanagement/agent/${data.id}` : '/tenantmanagement/agent-lead',
          data: prepareAgentClientFormValues(data),
          meta: {
            isShowError: true,
            errorMessageKey: 'List:errors.failedToSaveItem',
            isShowSuccess: true,
            successMessageKey: 'List:notification.saveSuccess',
          },
        }),
        invalidatesTags: [ECacheTag.LEAD_CLIENTS],
      }),
      createInviteLink: builder.mutation<{inviteUrl: string}, TLeadClient | TAgentClient>({
        query: (data) => ({
          method: 'POST',
          url: `/tenantmanagement/tenant-lead/${data.id}/generate-invite-url`,
          data,
          meta: {isShowError: true},
        }),
        invalidatesTags: (_, __, {id}) => [{id, type: ECacheTag.LEAD_CLIENTS}],
      }),
      checkPossibilityOfCreatingLeadClient: builder.query<
        boolean,
        Partial<Pick<TLeadClient, 'taxNumber' | 'contactName' | 'email'>>
      >({
        // @ts-expect-error: ok
        queryFn: async ({taxNumber, contactName, email}, listenerApi, extraOptions, baseQuery) => {
          const errors: ELeadClientCreatingError[] = [];

          if (taxNumber) {
            const {data: taxNumberData} = await baseQuery({
              method: 'GET',
              url: `/tenantmanagement/tenant-lead/${buildQueryString({taxNumber, contactName})}`,
            });

            if (taxNumberData) {
              errors.push(ELeadClientCreatingError.TAX_NUMBER_ALREADY_IN_USE);
            }
          }

          const {error} = await checkEmailQuery(baseQuery, email);

          if (error) {
            return {error: [...errors, ELeadClientCreatingError.EMAIL_ALREADY_IN_USE]};
          }

          if (errors.length) {
            return {error: errors};
          }

          return {data: true};
        },
      }),
      checkPossibilityOfCreatingAgentClient: builder.mutation<
        boolean,
        Partial<Pick<TAgentClient, 'taxNumber' | 'agentName' | 'email'>>
      >({
        // @ts-expect-error: ok
        queryFn: async ({email, taxNumber, agentName}, listenerApi, extraOptions, baseQuery) => {
          if (taxNumber || agentName) {
            const {data} = await baseQuery({
              method: 'GET',
              url: `/tenantmanagement/agent-lead${buildQueryString({taxNumber, agentName})}`,
            });

            if (data) {
              return {error: true};
            }
          }

          const {error} = await checkEmailQuery(baseQuery, email);

          if (error) {
            return {error: true};
          }

          return {data: true};
        },
      }),
      getTenantLeadDetails: builder.query<TLeadClient, TItemId | void>({
        query: (supportTenantId) => ({
          method: 'GET',
          url:
            supportTenantId !== undefined
              ? `/tenantmanagement/tenant-lead/by-tenant/${supportTenantId}`
              : '/tenantmanagement/tenant-lead/by-tenant',
          meta: {
            isShowError: true,
            roles:
              supportTenantId !== undefined
                ? and<ERoles>('ROLE_SUPPORT')
                : not<ERoles>('ROLE_BASIC_USER'),
          },
        }),
      }),
      getAgentLeadDetails: builder.query<TAgentClient, TItemId>({
        query: (tenantId) => ({
          method: 'GET',
          url: `/tenantmanagement/tenant-lead/by-tenant-without-overdraft/${tenantId}`,
          meta: {
            isShowError: true,
            roles: or<ERoles>('ROLE_ACCOUNT_MANAGER', 'ROLE_AGENT'),
          },
        }),
      }),
      saveTenantLeadDetails: builder.mutation<
        TLeadClient,
        Required<TTenantLeadDetailsPayload> & {tenantLeadId: TItemId}
      >({
        query: ({data, tenantLeadId}) => ({
          method: 'PATCH',
          url: `/tenantmanagement/tenant-lead/${tenantLeadId}`,
          data,
          meta: {isShowError: true},
        }),
        async onQueryStarted({supportTenantId}, {dispatch, queryFulfilled}) {
          try {
            const {data: newTenantLeadDetails} = await queryFulfilled;

            dispatch(
              api.util.updateQueryData(
                'getTenantLeadDetails',
                supportTenantId,
                () => newTenantLeadDetails,
              ),
            );
          } catch {}
        },
      }),
    };
  },
});

export const {
  useGetLeadClientsQuery,
  useGetLeadClientsAnalyticsQuery,
  useGetLeadClientQuery,

  useGetPartnersQuery,
  useCreateLeadClientMutation,
  useLazyCheckPossibilityOfCreatingLeadClientQuery,

  useGetAgentClientsQuery,
  useGetAgentClientQuery,
  useSaveAgentClientMutation,
  useCreateInviteLinkMutation,
  useCheckPossibilityOfCreatingAgentClientMutation,

  useGetTenantLeadDetailsQuery,
  useGetAgentLeadDetailsQuery,
  useSaveTenantLeadDetailsMutation,
} = api;
